import { getCookie, setCookie } from 'cookies-next'
import { useSearchParams } from 'next/navigation'
import { JsonType } from 'posthog-js'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useState } from 'react'

const COOKIE_PREFIX = 'exp_override_'
const isDev = process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production'

type ExperimentVariant = 'test' | 'control' | string

const useGetExperimentVariant = <PayloadType = JsonType>(
  key: string,
  {
    includePayload,
    localOverride,
    localPayload,
  }: {
    includePayload?: boolean
    localOverride?: ExperimentVariant
    localPayload?: PayloadType
  } = {}
) => {
  const posthog = usePostHog()
  const searchParams = useSearchParams()
  const [value, setValue] = useState<string | boolean | undefined>(
    posthog.getFeatureFlag(key)
  )
  const [payload, setPayload] = useState<PayloadType | undefined>(undefined)
  const [isReady, setIsReady] = useState(!includePayload && value != undefined)

  // Handles feature flag value resolution and PostHog feature flag loading
  useEffect(() => {
    // If in dev and localOverride is set, use that and return
    if (isDev && localOverride) {
      setValue(localOverride)
      if (includePayload) {
        setPayload(localPayload)
      }

      setIsReady(true)
      return
    }

    // Check for URL override first
    const flagParam = searchParams?.get('flag')
    if (flagParam) {
      const [flagKey, flagValue] = flagParam.split(':')
      if (flagKey === key) {
        setCookie(`${COOKIE_PREFIX}${key}`, flagValue, {
          expires: undefined, // Makes it a session cookie
        })
        setValue(flagValue)
        setIsReady(true)
        return
      }
    }

    // Check for cookie override
    const cookieValue = getCookie(`${COOKIE_PREFIX}${key}`)
    if (cookieValue) {
      setValue(cookieValue.toString())
      setIsReady(true)
      return
    }

    // If no overrides, set up PostHog
    // Check if the flag is already loaded
    const currentValue = posthog.getFeatureFlag(key)
    if (currentValue != undefined) {
      setValue(currentValue)

      if (includePayload) {
        const currentPayload = posthog.getFeatureFlagPayload(key)
        setPayload(currentPayload as PayloadType | undefined)
      }

      setIsReady(true)
    }

    // Load, and possibly update, the flag
    posthog.onFeatureFlags(() => {
      const newValue = posthog.getFeatureFlag(key)
      setValue(newValue)

      if (includePayload) {
        const newPayload = posthog.getFeatureFlagPayload(key)
        setPayload(newPayload as PayloadType | undefined)
      }
      setIsReady(true)
    })
  }, [posthog, key, searchParams, localOverride, includePayload, localPayload])

  const override = isDev ? localOverride : undefined
  const hasOverride =
    override !== undefined ||
    searchParams?.get('flag')?.split(':')[0] === key ||
    getCookie(`${COOKIE_PREFIX}${key}`) !== undefined

  return {
    isReady,
    hasOverride,
    loading: !isReady,
    isControl: override === 'control' || value === 'control',
    isTest: override === 'test' || value === 'test',
    value: override || value,
    payload,
  }
}

export default useGetExperimentVariant
